import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { fmtTime } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ClockWidget({ dailyHours = 8 }) {
  const [today, setToday] = useState({});
  const [breakMin, setBreakMin] = useState(15);

  const load = async () => {
    try {
      const r = await api.get("/attendance/today");
      setToday(r.data || {});
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const clockIn = async () => {
    try { await api.post("/attendance/clock-in"); toast.success("Clocked in"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const clockOut = async () => {
    try { await api.post("/attendance/clock-out"); toast.success("Clocked out"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const addBreak = async () => {
    try { await api.post(`/attendance/break?minutes=${breakMin}`); toast.success(`+${breakMin}m break`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const ci = today.clock_in ? new Date(today.clock_in) : null;
  const co = today.clock_out ? new Date(today.clock_out) : null;
  const end = co || new Date();
  const workedMin = ci ? Math.max(0, (end - ci) / 60000 - (today.break_minutes || 0)) : 0;
  const workedH = (workedMin / 60).toFixed(2);
  const required = dailyHours || 8;
  const remain = Math.max(0, required - workedMin / 60).toFixed(2);

  return (
    <div className="bg-white border border-[#E5E5E5] p-6 lg:p-8 mb-6" data-testid="admin-clock-widget">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <div className="eyebrow">My Session · Today</div>
          <div className="flex items-baseline gap-6 mt-4">
            <div>
              <div className="text-xs text-neutral-500">Clock In</div>
              <div className="font-head text-2xl font-semibold" data-testid="admin-clock-in-time">{fmtTime(today.clock_in)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Clock Out</div>
              <div className="font-head text-2xl font-semibold" data-testid="admin-clock-out-time">{fmtTime(today.clock_out)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Break</div>
              <div className="font-head text-2xl font-semibold">{today.break_minutes || 0}m</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Worked / Required</div>
              <div className="font-head text-2xl font-semibold">
                <span className="text-[#FF5500]">{workedH}</span>
                <span className="text-neutral-400 text-lg"> / {required}h</span>
              </div>
              <div className="text-xs text-neutral-500 mt-0.5">Remaining: {remain}h</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 min-w-[200px]">
          {!today.clock_in ? (
            <Button data-testid="admin-clock-in-button" onClick={clockIn}
              className="rounded-full h-12 px-8 bg-[#FF5500] hover:bg-[#E64D00] text-white font-semibold text-base">
              ▶ Clock In
            </Button>
          ) : !today.clock_out ? (
            <Button data-testid="admin-clock-out-button" onClick={clockOut}
              className="rounded-full h-12 px-8 bg-[#1F4E8C] hover:bg-[#163a68] text-white font-semibold text-base">
              ■ Clock Out
            </Button>
          ) : (
            <div className="text-center p-3 border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium" data-testid="admin-day-complete">Day Complete ✓</div>
          )}
          {today.clock_in && !today.clock_out && (
            <div className="flex gap-2">
              <Input type="number" value={breakMin} onChange={(e) => setBreakMin(+e.target.value || 0)}
                className="rounded-none border-[#E5E5E5] h-10" data-testid="admin-break-minutes" />
              <Button variant="outline" onClick={addBreak} data-testid="admin-add-break"
                className="rounded-none border-[#1F4E8C] text-[#1F4E8C] h-10">+ Break</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
